/**
 * People's routes (slice 6a §3, contracts §9.1) and their query keys. A
 * `people` hub event invalidates `['people']` and `['me']` (the workspace
 * line counts the crew): an invite sent or cancelled, a person joined or removed.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../app/api";
import { meKey } from "../../app/me";

export type Person = {
  id: string;
  name: string;
  initials: string;
  tint: string;
  email: string;
  role: "owner" | "member";
  /** The names of the apps they connected. */
  apps: string[];
  last_seen_at: string | null;
  created_at: string;
};

export type Invite = {
  id: string;
  email: string;
  invited_by: { id: string; name: string } | null;
  created_at: string;
  /** Null while the email couldn't be sent: "Couldn't send". */
  sent_at: string | null;
  expires_at: string;
  expired: boolean;
};

export type PeopleList = { people: Person[]; invites: Invite[] };

/** An invite sent: `link` only in mock mode, where nothing is emailed (§8). */
export type Sent = { invite: Invite; link?: string };

export const peopleKey = ["people"] as const;

export function usePeople({ enabled = true }: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: peopleKey,
    enabled,
    queryFn: () => api<PeopleList>("GET", "/api/people"),
  });
}

/** After any change: the list, and the workspace line's count. */
function useRefresh() {
  const client = useQueryClient();
  return () =>
    Promise.all([
      client.invalidateQueries({ queryKey: peopleKey }),
      client.invalidateQueries({ queryKey: meKey }),
    ]);
}

export function useInvite() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (email: string) => api<Sent>("POST", "/api/invites", { email }),
    onSuccess: refresh,
  });
}

export function useResend() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api<Sent>("POST", `/api/invites/${id}/resend`),
    onSuccess: refresh,
  });
}

export function useCancelInvite() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api("DELETE", `/api/invites/${id}`),
    onSuccess: refresh,
  });
}

export function useRemovePerson() {
  const refresh = useRefresh();
  return useMutation({
    mutationFn: (id: string) => api("DELETE", `/api/people/${id}`),
    onSuccess: refresh,
  });
}

// ── Joining (§3.3): no session ───────────────────────────────────────────

export type JoinView = {
  workspace: { name: string };
  invited_by: { name: string };
  email: string;
  people: Array<{ name: string; initials: string; tint: string }>;
};

export const joinKey = (token: string) => ["join", token] as const;

export function useJoinView(token: string) {
  return useQuery({
    queryKey: joinKey(token),
    queryFn: () => api<JoinView>("GET", `/api/join/${token}`),
    // An expired or spent link stays so.
    retry: false,
    retryOnMount: false,
  });
}

export function useJoin(token: string) {
  return useMutation({
    mutationFn: (input: { name: string; password: string }) =>
      api("POST", `/api/join/${token}`, input),
  });
}
